import { DateOnly } from '@if/core/date'
import { t } from '@if/core/i18n'
import { ECreditApplicationStatus, getECreditApplicationStatusList } from '@if/core/types'
import {
  ColumnDef,
  DataGrid,
  EnumChip,
  ErrorAlert,
  FieldsetCard,
  StatCard,
  useConfirmDialog,
  useErrorHandler,
  useIfUser,
  useSnackbar,
} from '@if/mui'
import { Box, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { useMerchant } from '@src/containers/App/MerchantProvider'
import { useMerchantByParentId } from '@src/data/api/merchants-api/merchants-api'
import { MerchantUserRow, useGetMerchantUsersQuery, useManageUserProfile } from '@src/data/api/user-api/user-api'
import { Merchant } from '@src/types/Merchant'
import { useCallback, useMemo, useState } from 'react'
import ManageUserDialogContent from './ManageUserDialogContent'
import { ManageUsersSkeleton } from './ManageUsersSkeleton'
import { useManageUsersColumns } from './manageUsers-hooks'

const PERIODS = [1, 3, 6, 12, 0]

const getDateValue = (months: number): DateOnly => {
  if (months === 0) return new DateOnly('1970-01-01')
  const now = new DateOnly()
  now.setMonth(now.getMonth() - months)
  return now
}

const periodLabel = (months: number): string =>
  months === 0 ? t('common.allStats') : `${months} ${months === 1 ? t('common.month') : t('common.months')}`

// Loads the merchant's users (+ subsidiaries for banner users) for the selected
// period. `keepPreviousData` (in the query) keeps the current rows on-screen while a
// period change refetches, so only the first load hits the pending/skeleton state.
const useMerchantUsers = (months: number) => {
  const currentUser = useIfUser()
  const merchant = useMerchant()
  const [subsidiaries] = useMerchantByParentId(
    currentUser?.isBannerUser && currentUser?.merchantId ? currentUser.merchantId : '',
  )
  const merchantIds: string[] = [merchant, ...(subsidiaries ?? [])]
    .filter((m): m is Merchant => m != null)
    .map((m) => m.id)
  return useGetMerchantUsersQuery({ merchantIds, createdOnDateLimit: getDateValue(months) })
}

/**
 * The Manage Users content: the user-count summary, the period toggle, the summary
 * stat tiles and the users grid, plus the activate/deactivate dialogs. Owns the data
 * fetch + state; the page frame (title) lives in `ManageUsersPage`.
 */
export const ManageUsersContent = () => {
  const [months, setMonths] = useState(1)
  const usersQuery = useMerchantUsers(months)
  const users = useMemo(() => usersQuery.data ?? [], [usersQuery.data])
  const currentUser = useIfUser()

  // Only banner users get subsidiary rows (see `useMerchantUsers`), so for everyone
  // else the merchant is the same on every row — drop the column rather than spend
  // its width on a constant, which also keeps the grid off a horizontal scrollbar
  // on narrower screens.
  const visibilityModel = useMemo(() => ({ merchant: !!currentUser?.isBannerUser }), [currentUser?.isBannerUser])

  const [manageUser] = useManageUserProfile()
  const { report } = useErrorHandler()
  const { showSuccess } = useSnackbar()

  // Toggle a user's account: on success close the dialog + pop a success snackbar;
  // on failure report through the shared error handler (snackbar/registry) and
  // return false to keep the dialog open for a retry. The mutation invalidates the
  // users list, so the row reflects its new status on refetch (`keepPreviousData`).
  const toggleAccount = useCallback(
    async ({ props: user }: { props: MerchantUserRow }) => {
      try {
        await manageUser({ user, accountEnabled: !user.accountEnabled })
        showSuccess(t('common.successful'))
      } catch (error) {
        report(error as Error)
        return false
      }
    },
    [manageUser, report, showSuccess],
  )

  const { open: openDeactivate } = useConfirmDialog<MerchantUserRow>({
    title: t('common.manageUserDialogTitle'),
    mode: 'destructive',
    maxWidth: 'sm',
    confirmLabel: t('common.confirmDeactivation'),
    message: ({ props: user }) => <ManageUserDialogContent user={user} activating={false} />,
    onConfirm: toggleAccount,
  })
  const { open: openActivate } = useConfirmDialog<MerchantUserRow>({
    title: t('common.manageUserDialogTitle'),
    maxWidth: 'sm',
    confirmLabel: t('common.confirmActivation'),
    message: ({ props: user }) => <ManageUserDialogContent user={user} activating={true} />,
    onConfirm: toggleAccount,
  })

  const manageAccount = useCallback(
    (user: MerchantUserRow) => void (user.accountEnabled ? openDeactivate : openActivate)(user),
    [openDeactivate, openActivate],
  )

  const columns: ColumnDef<MerchantUserRow>[] = useManageUsersColumns(manageAccount)

  // Aggregate application stats across the users for the summary tiles.
  const stats = useMemo(() => {
    const agg = users.reduce(
      (acc, u) => ({
        active: acc.active + (u.active ?? 0),
        completed: acc.completed + (u.completed ?? 0),
        draft: acc.draft + (u.draft ?? 0),
        expired: acc.expired + (u.expired ?? 0),
        cancelled: acc.cancelled + (u.cancelled ?? 0),
      }),
      { active: 0, completed: 0, draft: 0, expired: 0, cancelled: 0 },
    )
    return { ...agg, total: agg.active + agg.completed + agg.draft + agg.expired + agg.cancelled }
  }, [users])

  return (
    <Stack spacing={3}>
      {/* Header + period toggle always render — only the data region below loads. */}
      <FieldsetCard title={`${t('UserStatsHeaders.showingStatsFor')} · ${periodLabel(months)}`}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={months}
          onChange={(_: React.MouseEvent<HTMLElement>, v: number | null) => {
            if (v != null) setMonths(v)
          }}
        >
          {PERIODS.map((m) => (
            <ToggleButton key={m} value={m}>
              {periodLabel(m)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </FieldsetCard>

      {/* Skeleton the whole data region while fetching (first load + period change),
          so the stat tiles and grid load together. */}
      {usersQuery.isFetching ? (
        <ManageUsersSkeleton />
      ) : usersQuery.isError ? (
        <ErrorAlert error={usersQuery.error} />
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
              gap: 2,
            }}
          >
            <StatCard label={t('UserStatsHeaders.total')} value={stats.total} />
            {getECreditApplicationStatusList().map((status) => (
              <StatCard
                key={status}
                label={<EnumChip set={ECreditApplicationStatus} value={status} size="small" />}
                value={stats[status]}
              />
            ))}
          </Box>

          <DataGrid
            rows={users}
            columns={columns}
            visibilityModel={visibilityModel}
            sortModel={null}
            onSortModelChange={() => {}}
            paginated={false}
          />
        </>
      )}
    </Stack>
  )
}
